import { darkenHex } from '../core/Sprite.js';

/**
 * Cosmetics — the vanity catalog spent on with Glimmer (the third currency,
 * see systems/progression/Currency.js's calcGlimmer + SaveManager). Purely
 * visual, never touches combat stats, so there's no balance interaction with
 * Echoes/Shards. Four categories, each an independently-equipped loadout slot
 * (SaveManager.equippedCosmetics): body **skins**, projectile **trails**,
 * event-burst **fx** color themes, and overlay **hats** (data/sprites.js's
 * HAT_SPRITES).
 *
 * Skins are universal — they recolor whichever character is played (all five
 * share one silhouette, differing only by body hue), so a skin is just a
 * partial paletteOverride (keys R body / S shadow, mirroring the sprites' own
 * finishCreature derivation). null in a slot = the default look.
 *
 * Each entry is either `cost:` (bought with Glimmer) or `milestone:`
 * (auto-unlocked at run-end when a lifetime stat crosses its threshold — see
 * Game._endRun) — never both. Milestone pieces reuse stats that had no payout
 * before (grazes, Echo Duels won, highest Ascension, first clear).
 */

// Skin body+shadow override from a single hue — matches finishCreature's own
// 0.35 shadow derivation (data/sprites.js) so a recolored body is shaded the
// same way the base sprites are. Outline (O) and facing dot (K) keep their
// baked near-black values (not overridden), so the silhouette stays readable.
function skinPalette(body) {
  return { R: body, S: darkenHex(body, 0.35) };
}

export const COSMETICS = {
  // ---- Skins (universal body recolor) ----
  'skin-ember': { id: 'skin-ember', category: 'skin', name: 'Ember', description: 'A smouldering orange coat.', cost: 60, palette: skinPalette('#ff6b3d') },
  'skin-verdant': { id: 'skin-verdant', category: 'skin', name: 'Verdant', description: 'Deep forest green.', cost: 60, palette: skinPalette('#57d17a') },
  'skin-crimson': { id: 'skin-crimson', category: 'skin', name: 'Crimson', description: 'Bold arterial red.', cost: 80, palette: skinPalette('#ff4d5e') },
  'skin-violet': { id: 'skin-violet', category: 'skin', name: 'Violet', description: 'Regal deep purple.', cost: 80, palette: skinPalette('#b06bff') },
  'skin-frost': { id: 'skin-frost', category: 'skin', name: 'Frost', description: 'Pale glacial blue.', cost: 100, palette: skinPalette('#8ad8ff') },
  'skin-ascendant': { id: 'skin-ascendant', category: 'skin', name: 'Ascendant', description: 'The shimmer of those who climb. Earned, not bought.', milestone: { stat: 'highestAscension', value: 3, text: 'Reach Ascension 3' }, palette: skinPalette('#c9a8ff') },
  // Reprise memento (REPRISE.md §7): granted by RepriseMenu to anyone who
  // helped rebuild a season's world and fell the reformed King. Neither bought
  // (cost) nor stat-gated (milestone) — a third kind, flagged `memento`.
  'skin-rebuilder': { id: 'skin-rebuilder', category: 'skin', name: "Rebuilder's Mark", description: 'The teal sheen of those who raised Melodia from the unmade ground.', memento: true, palette: skinPalette('#6fe0d0') },

  // ---- Trails (recolor the player projectile trail stroke) ----
  'trail-ember': { id: 'trail-ember', category: 'trail', name: 'Ember Wake', description: 'A warm orange streak.', cost: 50, color: '#ff8a3d' },
  'trail-viridian': { id: 'trail-viridian', category: 'trail', name: 'Viridian Wake', description: 'A bright green streak.', cost: 50, color: '#4ade80' },
  'trail-azure': { id: 'trail-azure', category: 'trail', name: 'Azure Wake', description: 'A cool blue streak.', cost: 70, color: '#7e97ff' },
  'trail-duelist': { id: 'trail-duelist', category: 'trail', name: "Duelist's Wake", description: 'A magenta streak worn by veterans of the Echo Duel.', milestone: { stat: 'totalEchoDuelsWon', value: 5, text: 'Win 5 Echo Duels' }, color: '#ff5dcb' },

  // ---- FX (recolor the level-up / boss-kill particle bursts) ----
  'fx-gold': { id: 'fx-gold', category: 'fx', name: 'Golden Bloom', description: 'Bursts flare gold.', cost: 40, color: '#ffd166' },
  'fx-verdant': { id: 'fx-verdant', category: 'fx', name: 'Verdant Bloom', description: 'Bursts flare green.', cost: 40, color: '#4ade80' },
  'fx-rose': { id: 'fx-rose', category: 'fx', name: 'Rose Bloom', description: 'Bursts flare pink.', cost: 60, color: '#ff87b3' },
  'fx-grazeborn': { id: 'fx-grazeborn', category: 'fx', name: 'Grazeborn Bloom', description: 'Bursts flare cyan — for those who dance through fire.', milestone: { stat: 'totalGrazes', value: 500, text: 'Graze 500 bullets' }, color: '#7de8ff' },

  // ---- Hats (overlay sprite above the head, data/sprites.js HAT_SPRITES) ----
  'hat-cap': { id: 'hat-cap', category: 'hat', name: 'Cap', description: 'A jaunty teal cap.', cost: 60, spriteKey: 'cap' },
  'hat-tophat': { id: 'hat-tophat', category: 'hat', name: 'Top Hat', description: 'Dapper formalwear.', cost: 90, spriteKey: 'tophat' },
  'hat-halo': { id: 'hat-halo', category: 'hat', name: 'Halo', description: 'A ring of soft light.', cost: 120, spriteKey: 'halo' },
  'hat-crown': { id: 'hat-crown', category: 'hat', name: 'Sovereign Crown', description: 'Worn only by those who have cleared the gauntlet.', milestone: { stat: 'normalWins', value: 1, text: 'Clear the gauntlet once' }, spriteKey: 'crown' },
};

export const COSMETIC_CATEGORIES = ['skin', 'trail', 'fx', 'hat'];

/** Display label per category for the Wardrobe tab strip. */
export const COSMETIC_CATEGORY_LABELS = { skin: 'Skins', trail: 'Trails', fx: 'FX', hat: 'Hats' };

export function cosmeticsByCategory(category) {
  return Object.values(COSMETICS).filter((c) => c.category === category);
}

/** True once a milestone cosmetic's lifetime-stat threshold is met (see SaveManager.statistics). Non-milestone entries return false — they're bought, not earned. */
export function cosmeticMilestoneMet(cosmetic, statistics) {
  if (!cosmetic.milestone) return false;
  return (statistics?.[cosmetic.milestone.stat] ?? 0) >= cosmetic.milestone.value;
}
